import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    getStudents,
    createStudent,
    deleteStudent,
    type StudentData,
} from '../lib/api';
import { queryKeys } from '../lib/queryClient';

export function useStudents(token: string | null, schoolProfileId?: string) {
    return useQuery({
        queryKey: queryKeys.students.list(schoolProfileId),
        queryFn: () => getStudents(token!, schoolProfileId),
        enabled: !!token && !!schoolProfileId,
        staleTime: 1000 * 60 * 5,
    });
}

export function useCreateStudent(token: string | null, schoolProfileId?: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: Omit<StudentData, 'id'>) => createStudent(data, token!),

        onMutate: async (newStudent) => {
            await queryClient.cancelQueries({ queryKey: queryKeys.students.list(schoolProfileId) });
            const previousStudents = queryClient.getQueryData<StudentData[]>(queryKeys.students.list(schoolProfileId));

            const tempId = `temp-${Date.now()}`;
            queryClient.setQueryData<StudentData[]>(
                queryKeys.students.list(schoolProfileId),
                (old = []) => [...old, { ...newStudent, id: tempId } as StudentData]
            );

            return { previousStudents, tempId };
        },

        onSuccess: (createdStudent, _variables, context) => {
            queryClient.setQueryData<StudentData[]>(
                queryKeys.students.list(schoolProfileId),
                (old = []) => old.map(s => s.id === context?.tempId ? createdStudent : s)
            );
        },

        onError: (_err, _variables, context) => {
            if (context?.previousStudents) {
                queryClient.setQueryData(queryKeys.students.list(schoolProfileId), context.previousStudents);
            }
        },
    });
}

export function useDeleteStudent(token: string | null, schoolProfileId?: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => deleteStudent(id, token!),

        onMutate: async (deletedId) => {
            await queryClient.cancelQueries({ queryKey: queryKeys.students.list(schoolProfileId) });
            const previousStudents = queryClient.getQueryData<StudentData[]>(queryKeys.students.list(schoolProfileId));

            queryClient.setQueryData<StudentData[]>(
                queryKeys.students.list(schoolProfileId),
                (old = []) => old.filter(s => s.id !== deletedId)
            );

            return { previousStudents };
        },

        onError: (_err, _deletedId, context) => {
            if (context?.previousStudents) {
                queryClient.setQueryData(queryKeys.students.list(schoolProfileId), context.previousStudents);
            }
        },
    });
}
